/**
 * Deterministic 32-bit hash / PRNG helpers for procedural caves.
 * Mulberry32 + integer hash — no allocations, suitable for per-voxel sampling.
 */

export function hash2(x: number, z: number, seed: number): number {
  let h = (seed ^ Math.imul(x, 374761393) ^ Math.imul(z, 668265263)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  return (h ^ (h >>> 16)) >>> 0;
}

export function hash3(x: number, y: number, z: number, seed: number): number {
  let h =
    (seed ^
      Math.imul(x, 374761393) ^
      Math.imul(y, 668265263) ^
      Math.imul(z, 1274126177)) >>>
    0;
  h = Math.imul(h ^ (h >>> 13), 2246822519);
  h = Math.imul(h ^ (h >>> 16), 3266489917);
  return (h ^ (h >>> 16)) >>> 0;
}

/** Returns [0, 1) from a 32-bit hash. */
export function hashToUnit(h: number): number {
  return (h >>> 0) / 4294967296;
}

/**
 * Value noise in [0, 1) with bilinear interpolation on the XZ plane.
 * Frequency is in world-voxel units (larger = smoother).
 */
export function valueNoise2(
  x: number,
  z: number,
  seed: number,
  frequency: number,
): number {
  const fx = x * frequency;
  const fz = z * frequency;
  const x0 = Math.floor(fx);
  const z0 = Math.floor(fz);
  const tx = fx - x0;
  const tz = fz - z0;
  // Smoothstep
  const sx = tx * tx * (3 - 2 * tx);
  const sz = tz * tz * (3 - 2 * tz);

  const n00 = hashToUnit(hash2(x0, z0, seed));
  const n10 = hashToUnit(hash2(x0 + 1, z0, seed));
  const n01 = hashToUnit(hash2(x0, z0 + 1, seed));
  const n11 = hashToUnit(hash2(x0 + 1, z0 + 1, seed));

  const a = n00 + (n10 - n00) * sx;
  const b = n01 + (n11 - n01) * sx;
  return a + (b - a) * sz;
}
